import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useCalendar } from '../../contexts/CalendarContext';
import MonthView from './MonthView';
import WeekView from './WeekView';
import DayView from './DayView';
import Select from 'react-select';
import './Calendar.css';
import { customStyles } from '../../helpers/StyleUtils';

const Calendar = ({ onEventClick, onDateSelect, initialFilters = {}, events: propEvents = null }) => {
  const {
    currentDate,
    selectedDate,
    events: contextEvents,
    view,
    setView,
    goToPreviousMonth,
    goToNextMonth,
    goToPreviousWeek,
    goToNextWeek,
    goToPreviousDay,
    goToNextDay,
    goToToday,
    loading,
    setEventFilters,
    userOptions,
    clientOptions, 
    setOptions, 
    locationOptions, 
    statusOptions,
    talentGroups,
    getDaysForWeekView
  } = useCalendar();

  // Use prop events if provided, otherwise use context events
  const events = propEvents || contextEvents;

  // Selected filters
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedSet, setSelectedSet] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedTalentGroup, setSelectedTalentGroup] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Apply initial filters on mount
  useEffect(() => {
    if (Object.keys(initialFilters).length > 0 && !propEvents) {
      // Only apply filters if we're not using prop events
      // Set each filter and its select value
      if (initialFilters.bookingStatus) {
        const option = statusOptions.find(opt => opt.value === initialFilters.bookingStatus);
        setSelectedStatus(option || null);
      }
      
      if (initialFilters.userEmail) {
        const option = userOptions.find(opt => opt.value === initialFilters.userEmail);
        setSelectedUser(option || null);
      }
      
      if (initialFilters.client) {
        const option = clientOptions.find(opt => opt.value === initialFilters.client);
        setSelectedClient(option || null);
      }
      
      if (initialFilters.set) {
        const option = setOptions.find(opt => opt.value === initialFilters.set);
        setSelectedSet(option || null);
      }
      
      if (initialFilters.location) {
        const option = locationOptions.find(opt => opt.value === initialFilters.location);
        setSelectedLocation(option || null);
      }
      
      if (initialFilters.talentGroup) {
        const option = talentGroups.find(g => g.id === initialFilters.talentGroup);
        setSelectedTalentGroup(option ? { value: option.id, label: `${option.name} (${option.talentIds.length} talents)` } : null);
      }
      
      // Apply all filters at once to avoid multiple re-renders
      setEventFilters(initialFilters);
    }
  }, [initialFilters, userOptions, clientOptions, setOptions, locationOptions, statusOptions, talentGroups, propEvents]);
  
  // Handle filter changes
  const handleFilterChange = (type, option) => {
    const value = option?.value;
    
    switch(type) {
      case 'status':
        setSelectedStatus(option);
        setEventFilters({ bookingStatus: value });
        break;
      case 'user':
        setSelectedUser(option);
        setEventFilters({ userEmail: value });
        break;
      case 'client':
        setSelectedClient(option);
        setEventFilters({ client: value });
        break;
      case 'set':
        setSelectedSet(option);
        setEventFilters({ set: value });
        break;
      case 'location':
        setSelectedLocation(option);
        setEventFilters({ location: value });
        break;
      case 'talentGroup':
        setSelectedTalentGroup(option);
        setEventFilters({ talentGroup: value });
        break;
      default:
        break;
    }
  };

  // Handle search changes
  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setEventFilters({ searchTerm: term });
  };

  // Clear all filters including search
  const clearAllFilters = () => {
    setSelectedStatus(null);
    setSelectedUser(null);
    setSelectedClient(null);
    setSelectedSet(null);
    setSelectedLocation(null);
    setSelectedTalentGroup(null);
    setSearchTerm('');
    setEventFilters({});
  };

  // Handle navigation based on current view
  const handlePrevious = () => {
    switch (view) {
      case 'month':
        goToPreviousMonth();
        break;
      case 'week':
        goToPreviousWeek();
        break;
      case 'day':
        goToPreviousDay();
        break;
      default:
        break;
    }
  };

  const handleNext = () => {
    switch (view) {
      case 'month':
        goToNextMonth();
        break;
      case 'week':
        goToNextWeek();
        break;
      case 'day':
        goToNextDay();
        break;
      default:
        break;
    }
  };

  // Handle view change
  const handleViewChange = (newView) => {
    setView(newView);
  };

  // Handle event click
  const handleEventClick = (event) => {
    if (onEventClick) {
      onEventClick(event);
    }
  };
  
  // Handle date selection
  const handleDateSelect = (date) => {
    if (onDateSelect) {
      onDateSelect(date);
    }
  };

  // Render the appropriate view
  const renderView = () => {
    switch (view) {
      case 'month':
        return <MonthView onEventClick={handleEventClick} onDateSelect={handleDateSelect} />;
      case 'week':
        return (
          <WeekView
            currentDate={currentDate}
            selectedDate={selectedDate}
            events={events}
            onDateSelect={handleDateSelect}
            onEventClick={handleEventClick}
          />
        );
      case 'day':
        return <DayView onEventClick={handleEventClick} onDateSelect={handleDateSelect} />;
      default:
        return <MonthView onEventClick={handleEventClick} onDateSelect={handleDateSelect} />;
    }
  };


  return (
    <div className="calendar-container" >
      <div className="calendar-controls">
        <div className="calendar-title">
          <h2>
            {view === 'day'
              ? format(currentDate, 'MMMM d, yyyy')
              : view === 'week'
              ? `Week of ${format(currentDate, 'MMMM d, yyyy')}`
              : format(currentDate, 'MMMM yyyy')}
          </h2>
        </div>

        <div className="calendar-controls-buttons">
          <button
            className="calendar-button"
            onClick={goToToday}
          >
            Today
          </button>
          <button
            className="calendar-button"
            onClick={handlePrevious}
          >
            Previous
          </button>
          <button
            className="calendar-button"
            onClick={handleNext}
          >
            Next
          </button>
          <div className="calendar-view-selector">
            <button
              className={`calendar-button ${view === 'month' ? 'active' : ''}`}
              onClick={() => handleViewChange('month')}
            >
              Month
            </button>
            <button
              className={`calendar-button ${view === 'week' ? 'active' : ''}`}
              onClick={() => handleViewChange('week')}
            >
              Week
            </button>
            <button
              className={`calendar-button ${view === 'day' ? 'active' : ''}`}
              onClick={() => handleViewChange('day')}
            >
              Day
            </button>
          </div>
        </div>
      </div>
      
      <div style={{ marginTop: '10px', marginBottom: '30px' }}>
        <input
          type="text"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search talent, client, location..."
          className="inputtext search"
        />
        
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <Select
          value={selectedStatus}
          onChange={(option) => handleFilterChange('status', option)}
          options={statusOptions}
          placeholder="Filter by status"
          isClearable
          styles={customStyles}
          isLoading={loading}
        />
        <Select
          value={selectedUser}
          onChange={(option) => handleFilterChange('user', option)}
          options={userOptions}
          placeholder="Filter by User"
          isClearable
          styles={customStyles}
          isLoading={loading}
        />
        <Select
          value={selectedClient}
          onChange={(option) => handleFilterChange('client', option)}
          options={clientOptions}
          placeholder="Filter by Client"
          isClearable
          styles={customStyles}
          isLoading={loading}
        />
        <Select
          value={selectedSet}
          onChange={(option) => handleFilterChange('set', option)}
          options={setOptions}
          placeholder="Filter by Set"
          isClearable
          styles={customStyles}
          isLoading={loading}
        />
        <Select
          value={selectedLocation}
          onChange={(option) => handleFilterChange('location', option)}
          options={locationOptions}
          placeholder="Filter by Location"
          isClearable
          styles={customStyles}
          isLoading={loading}
        />
        <Select
          value={selectedTalentGroup}
          onChange={(option) => handleFilterChange('talentGroup', option)}
          options={talentGroups.map(group => ({
            value: group.id,
            label: `${group.name} (${group.talentIds.length} talents)`
          }))}
          placeholder="Filter by Talent Group"
          isClearable
          styles={customStyles}
          isLoading={loading}
        />
          </div>
        </div>
        
 
      </div>

      {loading ? (
        <div className="calendar-loading">Loading...</div>
      ) : (
        <div style={{ height: "calc(100% - 150px)" }}>
          {renderView()}
        </div>
      )}
    </div>
  );
};

export default Calendar; 